import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Button from "../components/common/button";
import StatCard from "../components/common/stat-card";
import RestaurantCard from "../components/common/restaurant-card";
import ActivityFeed from "../components/common/activity-feed";
import TrainingProgress from "../components/common/training-progress";
import ViewEmployeesPanel from "../components/common/view-employees-panel";
import LessonProgressPanel from "../components/common/lesson-progress-panel";
import ManageRestaurantPanel from "../components/common/manage-restaurant-panel";
import ManageMenuPanel from "../components/common/manage-menu-panel";
import PlatformOverview from "../components/common/platform-overview";
import AdminAnalytics from "../components/common/admin-analytics";
import OrganizationActivityFeed from "../components/common/organization-activity-feed";
import FinancialGrowthCard from "../components/common/financial-growth-card";
import { RestaurantsService } from "../services/Restaurants";
import { DashboardService } from "../services/dashboard";
import { MenuService } from "../services/MenuService";
import usaStates from "../usaStates";
import { viewEmployee } from "../services/Restaurants";
import {
  Menu,
  Target,
  Store,
  BarChart3,
  Users,
  GraduationCap,
  UserCheck,
  LogIn,
  TrendingDown,
  DollarSign,
} from "lucide-react";
import { FaUsers, FaCalendarAlt, FaExclamationTriangle } from "react-icons/fa";
import { FaListCheck } from "react-icons/fa6";
import { PiForkKnifeFill } from "react-icons/pi";

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [restaurants, setRestaurants] = useState([]);
  // Separate loading states
  const [loadingRestaurants, setLoadingRestaurants] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(true);
  const [directors, setDirectors] = useState([]);

  const [error, setError] = useState(null);
  const [activeSidePanel, setActiveSidePanel] = useState(null);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [stats, setStats] = useState({
    totalEmployees: 0,
    totalManagers: 0,
    menuItems: 0,
    wineList: 0,
    trainingRate: 0,
    missedTrainingRate: 0,
    menuUpdates: 0,
    averageChapterScore: 0,
    overdueEmployeesCount: 0,
    engagementRate: 0,
    churnedRestaurants: 0,
  });
  const [activityLogs, setActivityLogs] = useState([]);
  const [lessonProgress, setLessonProgress] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [userRole, setUserRole] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [dishAnalyticsData, setDishAnalyticsData] = useState([]);
  const [wineAnalyticsData, setWineAnalyticsData] = useState([]);
  const [beerAnalyticsData, setBeerAnalyticsData] = useState(null);

  const fetchData = () => {
    // Fetch Restaurants
    setLoadingRestaurants(true);
    RestaurantsService.getAllRestaurants("dashboard")
      .then((data) => {
        setRestaurants(data);
        setLoadingRestaurants(false);
      })
      .catch((err) => {
        console.error("Error fetching restaurants:", err);
        setError("Failed to fetch restaurants.");
        setLoadingRestaurants(false);
      });

    // Fetch Stats
    setLoadingStats(true);
    DashboardService.getAllStats()
      .then((data) => {
        setStats(data);
        setLoadingStats(false);
      })
      .catch((err) => {
        console.error("Error fetching stats:", err);
        setLoadingStats(false);
      });

    // Fetch Progress
    setLoadingProgress(true);
    DashboardService.getUserLessonProgress()
      .then((data) => {
        setLessonProgress(data);
        setLoadingProgress(false);
      })
      .catch((err) => {
        console.error("Error fetching progress:", err);
        setLoadingProgress(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const role = localStorage.getItem("userRole") || "";
    setUserRole(role);
    if (role === "manager" || role === "director") {
      setShowWelcome(true);
    }
  }, []);

  // Show login success toast if navigated with state
  useEffect(() => {
    if (location.state && location.state.successMessage) {
      setSuccessMessage(location.state.successMessage);
      setShowSuccess(true);
      // Remove the state so it doesn't show again on refresh or navigation
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  useEffect(() => {
    let timer;
    if (showSuccess) {
      timer = setTimeout(() => setShowSuccess(false), 4000);
    }
    return () => clearTimeout(timer);
  }, [showSuccess]);

  

  const activeRestaurant = React.useMemo(() => {
    return restaurants.length > 0 ? restaurants[0] : null;
  }, [restaurants]);

  const handleOpenSidePanel = async (panel, restaurant = null) => {
    try {
      setActiveSidePanel(panel);
      setSelectedRestaurant(restaurant);
      setBeerAnalyticsData(null);
      const [dishAnalytics, wineAnalytics, beerAnalytics] = await Promise.all([
        MenuService.getDishAnalytics(restaurant?.uuid),
        MenuService.getWineAnalytics(restaurant?.uuid),
        // A Beer failure must not take down the Food/Wine panel.
        MenuService.getBeerAnalytics(restaurant?.uuid).catch(() => null),
      ]);

      setDishAnalyticsData(dishAnalytics);
      setWineAnalyticsData(wineAnalytics);
      setBeerAnalyticsData(beerAnalytics);
    } catch (error) {
      console.log(error, "error");
    }
  };

  const handleCloseSidePanel = () => {
    setActiveSidePanel(null);
    setSelectedRestaurant(null);
  };

  return (
    <div
      className={`flex min-h-screen ${
        userRole === "director" || userRole === "super_admin"
          ? "bg-[#FFF9F2]"
          : "bg-background"
      }`}
    >
      {/* Login Success Toast (matches sticky msg color) */}
      {showSuccess && (
        <div
          className="fixed top-6 right-6 z-[5000] flex items-center bg-green-100 border border-green-400 text-green-900 px-6 py-3 rounded-b-md shadow-md w-full max-w-md text-base font-medium gap-3"
          style={{
            minWidth: 250,
            width: "auto",
            left: "unset",
            transform: "none",
            margin: 0,
          }}
        >
          <span className="flex-1 text-center">{successMessage}</span>
          <button
            onClick={() => setShowSuccess(false)}
            className="ml-2 text-green-900 hover:text-green-700 text-2xl font-bold focus:outline-none focus:ring-2 focus:ring-green-400 rounded-full px-2 transition"
            aria-label="Close"
            style={{ lineHeight: 1 }}
          >
            &times;
          </button>
        </div>
      )}

      <div className="flex-1 overflow-auto">
        <main className="p-3 ">
          {/* Stats Overview */}
          {userRole === "super_admin" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
              <StatCard
                variant="admin"
                title="Total Restaurants"
                value={stats.totalRestaurants ?? restaurants.length}
                icon={<Store className="w-5 h-5" />}
                subtext="Includes trial accounts"
                color="burgundy"
                loading={loadingStats}
              />
              <StatCard
                variant="admin"
                title="Active Restaurants"
                value={`${(stats.engagementRate || 0).toFixed(0)}%`}
                icon={<BarChart3 className="w-5 h-5" />}
                subtext="Currently training employees"
                color="green"
                loading={loadingStats}
              />
              <StatCard
                variant="admin"
                title="Active Users (All)"
                value={stats.totalEmployees.toLocaleString()}
                icon={<Users className="w-5 h-5" />}
                subtext="Directors, Managers, Employees"
                color="blue"
                loading={loadingStats}
              />
              <StatCard
                variant="admin"
                title="Avg. Training Completion"
                value={`${(stats.trainingRate || 0).toFixed(0)}%`}
                icon={<GraduationCap className="w-5 h-5" />}
                subtext="Global average progress"
                color="purple"
                loading={loadingStats}
              />
              <StatCard
                variant="admin"
                title="Monthly Active Users"
                value={Math.round(
                  stats.totalEmployees * (stats.engagementRate / 100)
                ).toLocaleString()}
                icon={<UserCheck className="w-5 h-5" />}
                subtext="Unique users in last 30 days"
                color="slate"
                loading={loadingStats}
              />

              <StatCard
                variant="admin"
                title="Avg. Sessions / User"
                value={(stats.averageChapterScore / 20).toFixed(1)}
                icon={<LogIn className="w-5 h-5" />}
                subtext="This month's average"
                color="yellow"
                loading={loadingStats}
              />

              <StatCard
                variant="admin"
                title="Churned Restaurants (30d)"
                value={stats.churnedRestaurants}
                icon={<TrendingDown className="w-5 h-5" />}
                subtext="Inactive for 30+ days"
                color="orange"
                loading={loadingStats}
              />
              <StatCard
                variant="admin"
                title="Current MRR"
                value={`$${(
                  stats.currentMRR || restaurants.length * 45
                ).toLocaleString()}`}
                icon={<DollarSign className="w-5 h-5" />}
                subtext="Monthly Recurring Revenue"
                color="teal"
                loading={loadingStats}
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
              <StatCard
                title="Employees in Training"
                value={stats.totalEmployees}
                icon={<FaUsers className="w-6 h-6" />}
                subtext="Currently enrolled"
                loading={loadingStats}
              />
              <StatCard
                title="Chapter Completion"
                value={`${(stats.trainingRate || 0).toFixed(0)}%`}
                icon={<FaListCheck className="w-6 h-6 mt-1" />}
                subtext="This restaurant's average"
                loading={loadingStats}
              />
              <StatCard
                title="Average Chapter Score"
                value={`${(stats.averageChapterScore || 0).toFixed(0)}%`}
                icon={<Target className="mt-1" />}
                subtext="Across completed chapters"
                loading={loadingStats}
              />
              <StatCard
                title="Engagement Rate"
                value={`${(stats.engagementRate || 0).toFixed(0)}%`}
                icon={<FaUsers className="w-6 h-6" />}
                subtext="Employee engagement"
                loading={loadingStats}
              />

              <StatCard
                title="Last Menu Update"
                value={`${(stats.lastMenuUpdateInDays || 0).toFixed(0)}d`}
                icon={<FaCalendarAlt className="w-6 h-6 mt-1" />}
                subtext="Since last update"
                loading={loadingStats}
              />
              <StatCard
                title="Overdue Lessons"
                value={stats.overdueEmployeesCount}
                icon={<FaExclamationTriangle className="w-6 h-6 mt-1" />}
                subtext="Employees with overdue lessons"
                loading={loadingStats}
              />
              <StatCard
                title="New Menu Items (30d)"
                value={stats.totalMenuItem || 0}
                icon={<PiForkKnifeFill className="w-6 h-6 mt-1" />}
                subtext="New dishes or wines"
                loading={loadingStats}
              />
            </div>
          )}

          {/* Platform Overview for Admin */}
          {userRole === "super_admin" && (
            <>
              <PlatformOverview
                data={directors}
                loading={loadingRestaurants}
              />
              <AdminAnalytics stats={stats} restaurants={restaurants} />
              <div className="mt-8">
                <OrganizationActivityFeed />
              </div>
              <div className="mt-12">
                <FinancialGrowthCard />
              </div>
            </>
          )}

          {/* Restaurants Section */}
          {userRole !== "super_admin" &&
            (loadingRestaurants || restaurants.length > 0) && (
              <div className="mb-6">
                <div className="flex justify-between items-center mb-4">
                  <h2
                    className={`text-xl font-bold text-[#881337] ${
                      userRole === "director" ? "text-2xl font-black" : ""
                    }`}
                  >
                    Restaurant Performance Overview
                  </h2>

                  {!loadingRestaurants && (
                    <Button
                      variant="primary"
                      size="md"
                      className={
                        userRole === "director"
                          ? "bg-[#881337] hover:bg-[#a11641] rounded-xl px-6 font-bold"
                          : ""
                      }
                      onClick={() => navigate("/restaurant-management")}
                    >
                      View All
                    </Button>
                  )}
                </div>
                <div
                  className={`grid grid-cols-1 ${
                    userRole === "director"
                      ? "md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                      : "gap-4"
                  }`}
                >
                  {loadingRestaurants ? (
                    <div className="flex items-center justify-center col-span-1 py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-200 border-t-red-500"></div>
                    </div>
                  ) : error ? (
                    <div className="text-red-500 col-span-1">{error}</div>
                  ) : (
                    restaurants
                      .slice(0, userRole === "director" ? 8 : 2)
                      .map((restaurant) => (
                        <RestaurantCard
                          variant={
                            userRole === "director" ? "director" : "default"
                          }
                          key={restaurant?._id}
                          name={restaurant?.name}
                          address={`${restaurant?.address.street}, ${
                            restaurant?.address.city
                          }, ${
                            restaurant.address.state
                              ? usaStates[restaurant.address.state] ||
                                restaurant.address.state
                              : ""
                          } ${restaurant?.address.zip}`}
                          status={restaurant?.status}
                          trainingCompletion={
                            restaurant?.dashboard?.lessonCompletionPercentage
                          }
                          trainingTrend={
                            restaurant?.dashboard?.lessonCompletionPercentage
                          }
                          overdueEmployees={
                            restaurant?.dashboard?.overDueEmployees
                          }
                          lastMenuUpdate={
                            restaurant?.dashboard?.lastMenuUpdated
                          }
                          employeesInTraining={
                            restaurant?.employees?.filter((emp) => emp !== null)
                              .length || 0
                          }
                          onManageRestaurant={() =>
                            handleOpenSidePanel("manageRestaurant", restaurant)
                          }
                          dashboard={restaurant?.dashboard}
                        />
                      ))
                  )}
                </div>
              </div>
            )}

          {/* Activity and Training Section */}
          {userRole !== "super_admin" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
              <ActivityFeed />
              {(loadingProgress || lessonProgress) && (
                <TrainingProgress
                  progress={lessonProgress}
                  stats={stats}
                  loading={loadingProgress}
                  restaurants={restaurants}
                />
              )}
            </div>
          )}
        </main>
      </div>

      {/* Side Panels */}
      {activeSidePanel === "viewEmployees" && (
        <ViewEmployeesPanel
          restaurant={selectedRestaurant}
          onClose={handleCloseSidePanel}
        />
      )}

      {activeSidePanel === "lessonProgress" && (
        <LessonProgressPanel
          restaurant={selectedRestaurant}
          onClose={handleCloseSidePanel}
        />
      )}

      {activeSidePanel === "manageRestaurant" && (
        <ManageRestaurantPanel
          restaurant={selectedRestaurant}
          onClose={handleCloseSidePanel}
          fetchData={fetchData}
        />
      )}

      {activeSidePanel === "manageMenu" && (
        <ManageMenuPanel
          restaurant={selectedRestaurant}
          onClose={handleCloseSidePanel}
          dishAnalyticsData={dishAnalyticsData}
          wineAnalyticsData={wineAnalyticsData}
          beerAnalyticsData={beerAnalyticsData}
          userRole={userRole}
        />
      )}

      {activeSidePanel === "viewEmployee" && (
        <ViewEmployeesPanel
          restaurant={selectedRestaurant}
          onClose={handleCloseSidePanel}
        />
      )}
    </div>
  );
}
